// src/components/admin/BillsManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, Search, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { propertiesAPI, agentsAPI, adminAPI } from '../../services/api';
import { getDisplayName, getPropertyBillPrimary, getPropertyBillSecondary } from '../../utils/displayName';
import { toast } from 'react-toastify';

const PAGE_SIZE = 1000;

function rawCollectionUnit(unit) {
  const v = unit?.collection_unit ?? unit?.collectionUnit;
  if (v === null || v === undefined) return null;
  return v;
}

const BillsManagement = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [agents, setAgents] = useState([]);
  const [collectionUnitSummaries, setCollectionUnitSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortByAgent, setSortByAgent] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollectionUnit, setFilterCollectionUnit] = useState('all');
  const [billsPage, setBillsPage] = useState(1);
  const [propertiesTotalInDb, setPropertiesTotalInDb] = useState(null);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const agentsData = await agentsAPI.getAll();
        if (!cancelled) setAgents(Array.isArray(agentsData) ? agentsData : []);
      } catch {
        if (!cancelled) setAgents([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const units = await adminAPI.getCollectionUnits();
        if (!cancelled) setCollectionUnitSummaries(Array.isArray(units) ? units : []);
      } catch {
        if (!cancelled) setCollectionUnitSummaries([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchBillsPage = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, page: billsPage };
      if (filterStatus === 'assigned') params.assigned = 'yes';
      if (filterStatus === 'pending') params.assigned = 'no';
      if (filterCollectionUnit !== 'all') params.collectionUnit = String(filterCollectionUnit);

      const propertiesData = await propertiesAPI.getAll(params);
      const raw = Array.isArray(propertiesData) ? { data: propertiesData } : propertiesData;
      setProperties(Array.isArray(raw?.data) ? raw.data : []);
      const total = raw?.meta?.total ?? 0;
      const computedPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
      const pages = Math.max(1, raw?.meta?.totalPages ?? computedPages);
      setPropertiesTotalInDb(total);
      setTotalPages(pages);
    } catch (error) {
      toast.error('Failed to load data');
      setProperties([]);
      setPropertiesTotalInDb(null);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [billsPage, filterStatus, filterCollectionUnit]);

  useEffect(() => {
    fetchBillsPage();
  }, [fetchBillsPage]);

  const setFilterStatusAndReset = (v) => {
    setFilterStatus(v);
    setBillsPage(1);
  };

  const setFilterCollectionUnitAndReset = (v) => {
    setFilterCollectionUnit(v);
    setBillsPage(1);
  };

  const getAmountPayable = (prop) => {
    const total = Number(prop.totalDue ?? prop.total_due);
    const current = Number(prop.currentAmount ?? prop.current_amount ?? 0);
    const arrears = Number(prop.arrears ?? 0);
    return total > 0 ? total : (current + arrears) || 0;
  };

  const filteredProps = properties
    .filter((prop) => {
      const q = searchQuery.toLowerCase().trim();
      if (!q) return true;
      return (
        (prop.billNo && prop.billNo.toLowerCase().includes(q)) ||
        (prop.newId && prop.newId.toLowerCase().includes(q)) ||
        (prop.valuationNo && prop.valuationNo.toLowerCase().includes(q)) ||
        (prop.streetName && prop.streetName.toLowerCase().includes(q))
      );
    })
    .sort((a, b) => {
      if (!sortByAgent) return 0;
      const agentA = agents.find((ag) => ag.id === a.assignedTo);
      const agentB = agents.find((ag) => ag.id === b.assignedTo);
      const nameA = getDisplayName(agentA) || 'Unassigned';
      const nameB = getDisplayName(agentB) || 'Unassigned';
      return nameA.localeCompare(nameB);
    });

  const rangeStart =
    propertiesTotalInDb === 0 ? 0 : (billsPage - 1) * PAGE_SIZE + 1;
  const rangeEnd =
    propertiesTotalInDb == null
      ? 0
      : Math.min(billsPage * PAGE_SIZE, propertiesTotalInDb);

  return (
    <div className='h-full flex flex-col'>
      <header className='p-4 sm:p-6 bg-[#001E3C] border-b border-white/10'>
        <div className='flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6'>
          <button
            type='button'
            onClick={() => navigate('/admin/dashboard')}
            className='glass-card p-2.5 sm:p-3 rounded-xl'>
            <ChevronLeft className='w-5 h-5 text-white' />
          </button>
          <h2 className='text-lg sm:text-xl font-black uppercase text-white'>Bills Management</h2>
        </div>

        <div className='grid grid-cols-3 gap-2 mb-4 text-white text-sm sm:text-base'>
          <button
            type='button'
            onClick={() => setFilterStatusAndReset('all')}
            className={`py-2 rounded-lg ${filterStatus === 'all' ? 'bg-[#FFC107] text-[#001021]' : 'glass-card'}`}>
            All
          </button>
          <button
            type='button'
            onClick={() => setFilterStatusAndReset('assigned')}
            className={`py-2 rounded-lg ${filterStatus === 'assigned' ? 'bg-[#00E676] text-[#001021]' : 'glass-card'}`}>
            Assigned
          </button>
          <button
            type='button'
            onClick={() => setFilterStatusAndReset('pending')}
            className={`py-2 rounded-lg ${filterStatus === 'pending' ? 'bg-[#FFC107] text-[#001021]' : 'glass-card'}`}>
            Pending
          </button>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-3 gap-3'>
          <select
            value={filterCollectionUnit}
            onChange={(e) => setFilterCollectionUnitAndReset(e.target.value)}
            className='w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none text-white'>
            <option value='all'>All Collection Units</option>
            {collectionUnitSummaries.map((u) => {
              const id = rawCollectionUnit(u);
              if (id == null) return null;
              return (
                <option key={String(id)} value={String(id)}>
                  {String(id).trim()}
                </option>
              );
            })}
          </select>

          <div className='relative lg:col-span-2'>
            <Search className='absolute left-4 top-3.5 w-5 h-5 text-white/30' />
            <input
              type='text'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none text-white'
              placeholder='Search this page (bill / valuation / street)...'
            />
          </div>
        </div>

        <button
          type='button'
          onClick={() => setSortByAgent(!sortByAgent)}
          className={`w-full mt-3 py-2 rounded-lg text-sm font-bold ${
            sortByAgent ? 'bg-[#00F5FF] text-[#001021]' : 'glass-card text-white'
          }`}>
          {sortByAgent ? 'Sorted by Agent' : 'Sort by Agent'}
        </button>

        {!loading && propertiesTotalInDb != null && (
          <p className='mt-3 text-xs text-white/50'>
            {propertiesTotalInDb === 0
              ? 'No bills in database for these filters.'
              : `Rows ${rangeStart}�${rangeEnd} of ${propertiesTotalInDb} (${PAGE_SIZE} per request) � page ${billsPage} of ${totalPages}`}
          </p>
        )}
      </header>

      <main className='flex-1 overflow-y-auto p-4 sm:p-6 no-scrollbar'>
        {loading ? (
          <div className='text-center py-12'>
            <p className='text-white/60'>Loading...</p>
          </div>
        ) : (
          <div className='space-y-4 max-w-6xl mx-auto'>
            {filteredProps.length === 0 ? (
              <p className='text-center text-white/50 text-sm'>No bills found</p>
            ) : (
              filteredProps.map((prop) => {
                const agent = agents.find((a) => a.id === prop.assignedTo);
                const agentName = getDisplayName(agent) || 'Unassigned';

                return (
                  <div key={prop.billNo || prop.id} className='glass-card p-4 text-white'>
                    <div className='flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3'>
                      <p className='font-mono font-bold break-all'>{getPropertyBillPrimary(prop)}</p>
                      {getPropertyBillSecondary(prop) && (
                        <p className='text-xs text-white/50 font-mono break-all'>Valuation: {getPropertyBillSecondary(prop)}</p>
                      )}
                      <span className={`status-badge w-fit ${prop.assignedTo ? 'status-delivered' : 'status-pending'}`}>
                        {prop.assignedTo ? 'Assigned' : 'Pending'}
                      </span>
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-3 gap-2 mb-3 text-sm'>
                      <p className='text-white/70'>Agent: <span className='text-white'>{agentName}</span></p>
                      <p className='text-white/70'>Collection Unit: <span className='text-white'>{prop.collectionUnit ?? '-'}</span></p>
                      <p className='font-bold text-[#FFC107] md:text-right'>
                        GHC{getAmountPayable(prop).toLocaleString()}
                      </p>
                    </div>

                    <div className='flex flex-col sm:flex-row sm:justify-between gap-1 text-xs text-white/60'>
                    <span>Due: {prop.dueDate
  ? new Date(prop.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  : '-'}
</span>
                      {prop.deliveryDate && <span>Delivered: {prop.deliveryDate}</span>}
                    </div>
                  </div>
                );
              })
            )}

            {!loading && totalPages > 1 && (
              <div className='flex flex-wrap items-center justify-center gap-3 pt-4 pb-8'>
                <button
                  type='button'
                  disabled={loading || billsPage <= 1}
                  onClick={() => setBillsPage((p) => Math.max(1, p - 1))}
                  className='glass-card flex items-center gap-2 px-4 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-40'>
                  <ChevronLeft className='w-5 h-5' />
                  Previous
                </button>
                <span className='text-white/70 text-sm'>
                  Page {billsPage} / {totalPages}
                </span>
                <button
                  type='button'
                  disabled={loading || billsPage >= totalPages}
                  onClick={() => setBillsPage((p) => p + 1)}
                  className='glass-card flex items-center gap-2 px-4 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-40'>
                  Next
                  <ChevronRight className='w-5 h-5' />
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default BillsManagement;
